import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import CategoryIcon from '@mui/icons-material/Category';
import api from '../services/api';

const StoreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);

  useEffect(() => {
    loadStore();
  }, [id]);

  const loadStore = async () => {
    try {
      const response = await api.get(`/stores/${id}`);
      setStore(response.data);
    } catch (error) {
      console.error('Erro ao carregar loja:', error);
    }
  };

  if (!store) {
    return (
      <Container>
        <Typography>Carregando...</Typography>
      </Container>
    );
  }

  return (
    <Box>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6, pt: 10 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ 
            mb: 3,
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'background.default',
              color: 'primary.main',
            },
          }}
        >
          Voltar
        </Button>

        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 5 }, 
            mb: 5,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <Grid container spacing={4}>
          {store.image_url && (
            <Grid item xs={12} md={4}>
              <Box
                component="img"
                src={store.image_url}
                alt={store.name}
                sx={{ 
                  width: '100%', 
                  height: 'auto',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={store.image_url ? 8 : 12}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom
              sx={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontWeight: 700,
                mb: 2,
              }}
            >
              {store.name}
            </Typography>

            {store.description && (
              <Typography 
                variant="body1" 
                paragraph
                sx={{
                  lineHeight: 1.8,
                  color: 'text.secondary',
                  fontSize: '1.05rem',
                }}
              >
                {store.description}
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {store.address && (
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5 }}>
                  <LocationOnIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {store.address}, {store.city} - {store.state}
                    {store.zip_code && `, ${store.zip_code}`}
                  </Typography>
                </Box>
              )}
              {store.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PhoneIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body1">{store.phone}</Typography>
                </Box>
              )}
              {store.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body1">{store.email}</Typography>
                </Box>
              )}
              {store.website && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LanguageIcon sx={{ color: 'primary.main' }} />
                  <Typography
                    variant="body1"
                    component="a"
                    href={store.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      textDecoration: 'none', 
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'primary.light',
                      },
                    }}
                  >
                    {store.website}
                  </Typography>
                </Box>
              )}
            </Box>

            {store.ranking && (
              <Box sx={{ 
                mt: 4,
                p: 3,
                backgroundColor: 'background.default',
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
                borderRadius: 1,
              }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Ranking
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Posição
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                      #{store.ranking.position || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Score Médio
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {store.ranking.averageScore?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Produtos
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {store.ranking.productsCount || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
        </Paper>

      <Typography 
        variant="h4" 
        gutterBottom
        sx={{
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontWeight: 700,
          mb: 3,
        }}
      >
        Produtos ({store.products?.length || 0})
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {store.products?.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}>
            <Card
              sx={{ 
                cursor: 'pointer', 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
              onClick={() => navigate(`/product/${product.id}`)}
            >
              {product.image_url ? (
                <CardMedia
                  component="img"
                  height="240"
                  image={product.image_url}
                  alt={product.name}
                  sx={{
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: 240,
                    backgroundColor: 'background.default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CategoryIcon sx={{ fontSize: 50, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontWeight: 600,
                    mb: 1,
                    minHeight: '3em',
                  }}
                >
                  {product.name}
                </Typography>
                <Chip 
                  label={product.category} 
                  size="small" 
                  sx={{ 
                    mt: 1,
                    backgroundColor: 'background.default',
                    color: 'primary.main',
                    fontWeight: 500,
                  }} 
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {(!store.products || store.products.length === 0) && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Esta loja ainda não possui produtos cadastrados.
        </Typography>
      )}
      </Container>
    </Box>
  );
};

export default StoreDetail;
